import * as THREE from "three";
import type { Structure, Node } from "@/physics/model";
import type { BuiltTower } from "@/game/towerSpec";
import type { AppurtenanceKind, MountedLoad } from "@/game/appurtenances";

/**
 * Renders the APPURTENANCES hung on a tower — the panel/sector antennas,
 * microwave dishes, RRU radios, omni whips and coax runs that `compileTower`
 * binds to structure nodes (see `Node.mountedLoads`). The physics already
 * applies their weight + wind; this view makes them VISIBLE so "adding
 * equipment" actually changes the picture on the tower.
 *
 * One `THREE.Group` per mounted load holds the little model for that kind. The
 * model is built once in local space around the origin, offset OUTWARD from the
 * node by the load's `standoff` along its `azimuth`, and faced outward. Each
 * frame `update()` just re-parents that group to the (deforming) node's live
 * position — so during a collapse the antennas travel and fall with the steel.
 * No per-frame allocation: the offset vector is precomputed and the hot loop
 * only writes `group.position`.
 *
 * Azimuth convention matches the compiler (towerSpec.compileTower): the load's
 * mount node is chosen by `atan2(z, x)` against the azimuth, i.e. azimuth 0 is
 * +x and it sweeps toward +z. We offset with (cos, 0, sin) to match.
 */

// Equipment reads light grey against the green→red steel; dishes are off-white.
const C_EQUIP = 0xced6dd; // panels / RRU / omni body — light equipment grey
const C_DISH = 0xeef0ec; // dish reflector — off-white
const C_DARK = 0x2a323c; // mounts / coax — dark grey, like the joints

// Plausible metres-at-tower-scale sizes.
const PANEL_H = 2.0; // sector panel ~2 m tall
const PANEL_W = 0.3; // ~300 mm wide
const PANEL_T = 0.12; // thin
const RRU_SIZE = 0.4; // ~0.4 m boxy radio
const OMNI_H = 1.6; // whip ~1.6 m
const OMNI_R = 0.03; // thin rod
const DEFAULT_DISH_D = 0.6; // fallback if the item carries no diameter
const COAX_R = 0.05; // feeder bundle tube radius

interface LoadItem {
  node: Node; // live structure node this load follows
  group: THREE.Group; // holds the model; only its .position is touched per frame
  // Precomputed outward offset from the node (standoff along azimuth). The
  // model inside `group` is already faced/positioned in this local frame, so
  // per-frame we only add this to the node position.
  offX: number;
  offY: number;
  offZ: number;
}

export class AppurtenanceView {
  readonly group = new THREE.Group();
  private structure: Structure;
  private items: LoadItem[] = [];
  // Shared materials (disposed once in dispose()).
  private matEquip = new THREE.MeshStandardMaterial({
    color: C_EQUIP,
    metalness: 0.2,
    roughness: 0.6,
  });
  private matDish = new THREE.MeshStandardMaterial({
    color: C_DISH,
    metalness: 0.1,
    roughness: 0.75,
    side: THREE.DoubleSide,
  });
  private matDark = new THREE.MeshStandardMaterial({
    color: C_DARK,
    metalness: 0.4,
    roughness: 0.6,
  });
  // Geometries are shared per kind where possible; tracked for disposal.
  private geometries: THREE.BufferGeometry[] = [];

  constructor(built: BuiltTower) {
    this.structure = built.structure;

    for (const node of this.structure.nodes) {
      const mounted = node.mountedLoads;
      if (!mounted || mounted.length === 0) continue;
      for (const nml of mounted) {
        this.addLoad(node, nml.load);
      }
    }

    this.update();
  }

  private addLoad(node: Node, load: MountedLoad): void {
    // Small outward standoff only — keep equipment hugging the tower face. (The
    // appurtenance's own `standoff` can be a big structural mount-arm value that
    // would fling a dish/panel far off the tower, so we don't use it for the
    // visual offset.)
    const standoff = Math.min(1.2, Math.max(0.15, load.standoff ?? 0.45));
    // Offset radially OUTWARD from the tower axis through the mount node, so the
    // equipment always sits just off that node's face and never floats out into
    // empty space when the requested azimuth doesn't line up with a face.
    const rr = Math.hypot(node.x, node.z);
    let cos: number;
    let sin: number;
    if (rr > 1e-3) {
      cos = node.x / rr;
      sin = node.z / rr;
    } else {
      const az = load.azimuth ?? 0;
      cos = Math.cos(az);
      sin = Math.sin(az);
    }
    const faceAz = Math.atan2(sin, cos);

    const g = new THREE.Group();
    // Orient the whole model so its local +X points OUTWARD along the azimuth
    // (compiler uses atan2(z,x), so outward = (cos, 0, sin)). A yaw about +Y by
    // `az` maps local +X → (cos,0,sin); the per-kind builders below then work in
    // a simple "outward = +X" frame.
    g.rotation.y = -faceAz;

    this.buildModel(g, load.appurtenance.kind, load);

    const item: LoadItem = {
      node,
      group: g,
      offX: cos * standoff,
      offY: 0,
      offZ: sin * standoff,
    };
    this.items.push(item);
    this.group.add(g);
  }

  /**
   * Build the little model for one load into `g`, in a local frame where +X is
   * OUTWARD (away from the mast) and +Y is up. Meshes are added as children so
   * update() only needs to move the parent group.
   */
  private buildModel(g: THREE.Group, kind: AppurtenanceKind, load: MountedLoad): void {
    switch (kind) {
      case "panel": {
        // Upright thin flat box, its face pointing outward (+X). Width across
        // the face (Z), height up (Y), thin in the outward direction (X).
        const geo = new THREE.BoxGeometry(PANEL_T, PANEL_H, PANEL_W);
        this.geometries.push(geo);
        const m = new THREE.Mesh(geo, this.matEquip);
        m.castShadow = true;
        g.add(m);
        break;
      }
      case "rru": {
        // Small boxy radio tucked behind the mount (slightly inboard, offset to
        // one side) so it reads as separate kit beside a panel.
        const geo = new THREE.BoxGeometry(RRU_SIZE, RRU_SIZE * 1.1, RRU_SIZE * 0.7);
        this.geometries.push(geo);
        const m = new THREE.Mesh(geo, this.matEquip);
        m.castShadow = true;
        m.position.set(-RRU_SIZE * 0.5, 0, RRU_SIZE * 0.6);
        g.add(m);
        break;
      }
      case "omni": {
        // Thin vertical whip rising above the node.
        const geo = new THREE.CylinderGeometry(OMNI_R, OMNI_R, OMNI_H, 6);
        this.geometries.push(geo);
        const m = new THREE.Mesh(geo, this.matEquip);
        m.castShadow = true;
        m.position.set(0, OMNI_H * 0.5, 0); // base at the node, rod rising up
        g.add(m);
        break;
      }
      case "dish": {
        // Parabolic microwave dish aimed outward (+X): a shallow cone reflector
        // (open face outward) plus a thin rim disc, scaled by diameter.
        const d = load.appurtenance.diameter ?? DEFAULT_DISH_D;
        const r = d * 0.5;
        const depth = d * 0.28; // shallow parabola proxy

        // Reflector: a cone, apex inboard, open mouth outward. Cone axis is +Y
        // by default; rotate so the axis points outward (+X): open face outward.
        const cone = new THREE.ConeGeometry(r, depth, 20, 1, true);
        this.geometries.push(cone);
        const dish = new THREE.Mesh(cone, this.matDish);
        dish.castShadow = true;
        // Cone tip at +Y apex; rotate -90° about Z so axis → +X, apex inboard.
        dish.rotation.z = Math.PI / 2;
        dish.position.set(-depth * 0.5, 0, 0);
        g.add(dish);

        // Back plate / rim so the dish reads solid from behind.
        const disc = new THREE.CylinderGeometry(r, r, d * 0.05, 20);
        this.geometries.push(disc);
        const back = new THREE.Mesh(disc, this.matDish);
        back.rotation.z = Math.PI / 2; // face outward
        back.position.set(-depth, 0, 0);
        g.add(back);

        // Feed boom: a thin rod from the dish center out to the focal point.
        const boomGeo = new THREE.CylinderGeometry(r * 0.04, r * 0.04, depth * 1.6, 6);
        this.geometries.push(boomGeo);
        const boom = new THREE.Mesh(boomGeo, this.matDark);
        boom.rotation.z = Math.PI / 2;
        boom.position.set(depth * 0.3, 0, 0);
        g.add(boom);
        break;
      }
      case "coax": {
        // Cheap feeder run: a thin vertical tube down the face. Length tracks the
        // run (defaults to mount height); we draw a representative visible stub so
        // it reads as cabling without dominating the tower.
        const run = load.runLength ?? load.height;
        const drawLen = Math.min(run, 4); // cap so tall runs don't overwhelm
        const geo = new THREE.CylinderGeometry(COAX_R, COAX_R, drawLen, 6);
        this.geometries.push(geo);
        const m = new THREE.Mesh(geo, this.matDark);
        m.position.set(0, -drawLen * 0.5, 0); // hangs down from the node
        g.add(m);
        break;
      }
    }
  }

  /**
   * Re-position every appurtenance to follow its (deforming) node. Cheap: reads
   * the stored live node position and adds the precomputed outward offset. No
   * allocation in the loop. The load's orientation is baked into the group's
   * local transform, so only the position needs updating each frame.
   */
  update(): void {
    for (const it of this.items) {
      const n = it.node;
      it.group.position.set(n.x + it.offX, n.y + it.offY, n.z + it.offZ);
    }
  }

  dispose(): void {
    for (const geo of this.geometries) geo.dispose();
    this.geometries.length = 0;
    this.matEquip.dispose();
    this.matDish.dispose();
    this.matDark.dispose();
    this.items.length = 0;
    this.group.clear();
  }
}
